import { useMemo, useState } from "react";
import clsx from "clsx";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { HealthDay, HeartRateZoneRange } from "../lib/types";
import {
  fmtCalories,
  fmtDurationMinutes,
  fmtDurationSeconds,
  fmtFullDate,
  fmtMiles,
  fmtMinutes,
  fmtMonthDay,
  fmtNumber,
} from "../lib/format";

type DetailTab = "day" | "week" | "month";

interface AzmDetailProps {
  rows: HealthDay[];
}

const TABS: { id: DetailTab; label: string }[] = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
];

const AZM_GOAL = 22;
const AZM_COLORS = {
  fatBurn: "#06b6d4",
  cardio: "#2563eb",
  peak: "#7c3aed",
};

export default function AzmDetail({ rows }: AzmDetailProps) {
  const [tab, setTab] = useState<DetailTab>("week");
  const visibleRows = useMemo(() => rangeRows(rows, tab), [rows, tab]);
  const chartRows = useMemo(() => dailyRows(visibleRows), [visibleRows]);
  const totals = useMemo(() => summarize(visibleRows), [visibleRows]);
  const today = rows.at(-1);
  const goalHits = chartRows.filter((r) => r.total >= r.goal).length;

  if (!today || !rows.length) return null;

  return (
    <section aria-label="Active Zone Minutes detail" className="card animate-slide-up p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">
            Active Zone Minutes
          </p>
          <h2 className="mt-1 text-xl font-semibold tracking-tight">
            {tab === "day" ? fmtFullDate(today.date) : rangeLabel(visibleRows)}
          </h2>
        </div>
        <div className="grid grid-cols-3 rounded-lg bg-zinc-100 p-1 text-sm dark:bg-zinc-800">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={clsx(
                "rounded-md px-3 py-1.5 font-medium transition-colors",
                tab === item.id
                  ? "bg-white text-zinc-950 shadow-sm dark:bg-zinc-950 dark:text-zinc-50"
                  : "text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
        <div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={chartRows}
              margin={{ top: 8, right: 8, left: -14, bottom: 0 }}
              barCategoryGap={4}
            >
              <CartesianGrid
                stroke="var(--grid)"
                strokeDasharray="3 3"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                interval={Math.max(0, Math.floor(chartRows.length / 8))}
                tick={{ fill: "var(--fg-muted)", fontSize: 11 }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tick={{ fill: "var(--fg-muted)", fontSize: 11 }}
                width={34}
              />
              <ReferenceLine
                y={AZM_GOAL}
                stroke="var(--fg-muted)"
                strokeDasharray="4 4"
                ifOverflow="extendDomain"
              />
              <Tooltip
                cursor={{ fill: "rgba(120,120,120,0.08)" }}
                content={<AzmTooltip />}
              />
              <Bar
                dataKey="fatBurn"
                stackId="azm"
                fill={AZM_COLORS.fatBurn}
                radius={[0, 0, 3, 3]}
              />
              <Bar dataKey="cardio" stackId="azm" fill={AZM_COLORS.cardio} />
              <Bar
                dataKey="peak"
                stackId="azm"
                fill={AZM_COLORS.peak}
                radius={[3, 3, 0, 0]}
              >
                {chartRows.map((row) => (
                  <Cell key={row.key} opacity={row.total >= row.goal ? 1 : 0.62} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-zinc-500">
            <LegendDot color={AZM_COLORS.fatBurn} label="Fat burn" />
            <LegendDot color={AZM_COLORS.cardio} label="Cardio" />
            <LegendDot color={AZM_COLORS.peak} label="Peak" />
            <span className="tnum">{goalHits}/{chartRows.length} goals hit</span>
          </div>
        </div>

        <div className="space-y-4">
          <ZoneSummary
            title="AZM zones"
            total={totals.azm.total}
            rows={[
              {
                label: "Fat burn",
                value: totals.azm.fatBurn,
                color: AZM_COLORS.fatBurn,
              },
              {
                label: "Cardio",
                value: totals.azm.cardio,
                color: AZM_COLORS.cardio,
              },
              { label: "Peak", value: totals.azm.peak, color: AZM_COLORS.peak },
            ]}
            unit="min"
          />
          <ZoneSummary
            title="Heart-rate zones"
            total={totals.hr.light + totals.hr.moderate + totals.hr.vigorous + totals.hr.peak}
            rows={[
              { label: "Light", value: totals.hr.light, color: "#22c55e", formatter: fmtDurationSeconds },
              { label: "Moderate", value: totals.hr.moderate, color: "#f59e0b", formatter: fmtDurationSeconds },
              { label: "Vigorous", value: totals.hr.vigorous, color: "#ef4444", formatter: fmtDurationSeconds },
              { label: "Peak", value: totals.hr.peak, color: "#7c3aed", formatter: fmtDurationSeconds },
            ]}
          />
          <Thresholds zones={today.heartRateZones} />
        </div>
      </div>
    </section>
  );
}

interface ChartRow {
  key: string;
  label: string;
  title: string;
  fatBurn: number;
  cardio: number;
  peak: number;
  total: number;
  goal: number;
  day: HealthDay;
}

function dailyRows(rows: HealthDay[]): ChartRow[] {
  return rows.map((row) => ({
    key: row.date,
    label: fmtMonthDay(row.date),
    title: fmtFullDate(row.date),
    fatBurn: row.activeZoneMinutes.fatBurn,
    cardio: row.activeZoneMinutes.cardio,
    peak: row.activeZoneMinutes.peak,
    total: row.activeZoneMinutes.total,
    goal: AZM_GOAL,
    day: row,
  }));
}

function rangeRows(rows: HealthDay[], tab: DetailTab): HealthDay[] {
  const count = tab === "day" ? 1 : tab === "week" ? 7 : 28;
  return rows.slice(-count);
}

function rangeLabel(rows: HealthDay[]): string {
  if (!rows.length) return "";
  return `${fmtMonthDay(rows[0].date)} – ${fmtMonthDay(rows[rows.length - 1].date)}`;
}

function summarize(rows: HealthDay[]) {
  return {
    azm: {
      fatBurn: sum(rows, (r) => r.activeZoneMinutes.fatBurn),
      cardio: sum(rows, (r) => r.activeZoneMinutes.cardio),
      peak: sum(rows, (r) => r.activeZoneMinutes.peak),
      total: sum(rows, (r) => r.activeZoneMinutes.total),
    },
    hr: {
      light: sum(rows, (r) => r.timeInHeartRateZones.lightSeconds),
      moderate: sum(rows, (r) => r.timeInHeartRateZones.moderateSeconds),
      vigorous: sum(rows, (r) => r.timeInHeartRateZones.vigorousSeconds),
      peak: sum(rows, (r) => r.timeInHeartRateZones.peakSeconds),
    },
  };
}

function ZoneSummary({
  title,
  total,
  rows,
  unit,
}: {
  title: string;
  total: number;
  rows: { label: string; value: number; color: string; formatter?: (n: number) => string }[];
  unit?: string;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="tnum text-xs text-zinc-500">
          {unit === "min" ? fmtMinutes(total) : fmtDurationSeconds(total)}
        </span>
      </div>
      <div className="mt-2 space-y-2">
        {rows.map((row) => {
          const pct = total > 0 ? Math.max(2, (row.value / total) * 100) : 0;
          return (
            <div key={row.label}>
              <div className="mb-1 flex justify-between gap-3 text-xs">
                <span className="text-zinc-500">{row.label}</span>
                <span className="tnum">{row.formatter ? row.formatter(row.value) : fmtMinutes(row.value)}</span>
              </div>
              <div className="h-2 rounded-full bg-zinc-100 dark:bg-zinc-800">
                <div
                  className="h-2 rounded-full"
                  style={{ width: `${pct}%`, backgroundColor: row.color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Thresholds({
  zones,
}: {
  zones: HealthDay["heartRateZones"];
}) {
  const safeZones = zones ?? {
    light: null,
    moderate: null,
    vigorous: null,
    peak: null,
  };
  const rows: [string, HeartRateZoneRange | null][] = [
    ["Light", safeZones.light],
    ["Moderate", safeZones.moderate],
    ["Vigorous", safeZones.vigorous],
    ["Peak", safeZones.peak],
  ];
  return (
    <div>
      <h3 className="text-sm font-semibold">Heart-rate thresholds</h3>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {rows.map(([label, range]) => (
          <div
            key={label}
            className="rounded-lg border border-zinc-200 px-3 py-2 dark:border-zinc-800"
          >
            <div className="text-xs text-zinc-500">{label}</div>
            <div className="tnum text-sm font-medium">
              {range
                ? `${range.minBeatsPerMinute}-${range.maxBeatsPerMinute}`
                : "—"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

interface TooltipProps {
  active?: boolean;
  payload?: { payload: ChartRow }[];
}

function AzmTooltip({ active, payload }: TooltipProps) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const day = row.day;
  return (
    <div className="card w-72 px-3 py-2 text-xs shadow-md">
      <div className="font-medium">{row.title}</div>
      <div className="tnum mt-1 font-semibold">
        {fmtMinutes(row.total)} <span className="font-normal text-zinc-500">AZM</span>
      </div>
      <div className="mt-1 grid grid-cols-3 gap-2 text-zinc-500">
        <span>Fat {fmtMinutes(row.fatBurn)}</span>
        <span>Cardio {fmtMinutes(row.cardio)}</span>
        <span>Peak {fmtMinutes(row.peak)}</span>
      </div>
      <div className="mt-1 text-zinc-500">
        {row.total >= row.goal ? "Goal hit" : `${fmtMinutes(row.goal - row.total)} to goal`}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-zinc-200 pt-2 text-zinc-500 dark:border-zinc-800">
        <TooltipMetric label="Steps" value={fmtNumber(day.steps)} />
        <TooltipMetric label="Miles" value={fmtMiles(day.distanceMeters)} />
        <TooltipMetric label="Calories" value={fmtCalories(day.calories)} />
        <TooltipMetric
          label="Sleep"
          value={fmtDurationMinutes(day.sleep?.minutesAsleep ?? 0)}
        />
        <TooltipMetric
          label="Vigorous HR"
          value={fmtDurationSeconds(day.timeInHeartRateZones.vigorousSeconds)}
        />
        <TooltipMetric
          label="Peak HR"
          value={fmtDurationSeconds(day.timeInHeartRateZones.peakSeconds)}
        />
        <TooltipMetric
          label="Resting HR"
          value={day.restingHeartRate === null ? "—" : `${day.restingHeartRate}`}
        />
        <TooltipMetric
          label="HRV"
          value={
            day.heartRateVariabilityMs === null
              ? "—"
              : `${Math.round(day.heartRateVariabilityMs)}ms`
          }
        />
      </div>
    </div>
  );
}

function TooltipMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <div>{label}</div>
      <div className="tnum truncate font-medium text-zinc-900 dark:text-zinc-100">
        {value}
      </div>
    </div>
  );
}

function sum<T>(items: T[], pick: (item: T) => number): number {
  return items.reduce((total, item) => total + pick(item), 0);
}
